import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import * as rationingService from '../services/rationingService';

// Create the context
const RationingContext = createContext();

// Custom hook to use the rationing context
export const useRationing = () => useContext(RationingContext);

// Provider component
export const RationingProvider = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rationingInfo, setRationingInfo] = useState({
    rationStatus: 'SUSPENDED',
    appliedRation: 0,
    remainingRation: 0,
    lastUpdated: new Date().toISOString(),
    percentRemaining: 0
  });

  // Fetch rationing information when user changes
  useEffect(() => {
    if (!user || !user.accountUuid) {
      setLoading(false);
      return;
    }

    const fetchRationingData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch rationing data using the service
        const data = await rationingService.getRationingData(user.accountUuid);
        
        if (data) {
          // Process API response
          const rationData = {
            rationStatus: data.rationStatus || data.status || 'SUSPENDED',
            appliedRation: data.appliedRation || 0,
            remainingRation: data.remainingRation || 0,
            lastUpdated: typeof data.lastUpdated === 'number' 
              ? new Date(data.lastUpdated).toISOString() 
              : data.lastUpdated || new Date().toISOString(),
            percentRemaining: data.percentRemaining || 0
          };
          
          // If the API doesn't provide percentRemaining, calculate it
          if (!data.percentRemaining && data.appliedRation > 0) {
            rationData.percentRemaining = (data.remainingRation / data.appliedRation) * 100;
          }
          
          setRationingInfo(rationData);
          // console.log('Rationing data loaded in context:', rationData);
        } else {
          // Fallback to default suspended state
          setRationingInfo({
            rationStatus: 'SUSPENDED',
            appliedRation: 0,
            remainingRation: 0,
            lastUpdated: new Date().toISOString(),
            percentRemaining: 0
          });
          setError('Could not retrieve rationing data.');
        }
      } catch (error) {
        // console.error('Error fetching rationing data in context:', error);
        setError('Failed to fetch rationing data.');
        
        // Set fallback data
        setRationingInfo({
          rationStatus: 'SUSPENDED',
          appliedRation: 0,
          remainingRation: 0,
          lastUpdated: new Date().toISOString(),
          percentRemaining: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRationingData();
  }, [user]);

  // Manual refresh function
  const refreshRationingData = async () => {
    if (!user || !user.accountUuid) {
      return;
    }
    
    try {
      setLoading(true);
      const data = await rationingService.getRationingData(user.accountUuid);
      
      if (data) {
        const rationData = {
          rationStatus: data.rationStatus || data.status || 'SUSPENDED',
          appliedRation: data.appliedRation || 0,
          remainingRation: data.remainingRation || 0,
          lastUpdated: typeof data.lastUpdated === 'number' 
            ? new Date(data.lastUpdated).toISOString() 
            : data.lastUpdated || new Date().toISOString(),
          percentRemaining: data.percentRemaining || 0
        };
        
        if (!data.percentRemaining && data.appliedRation > 0) {
          rationData.percentRemaining = (data.remainingRation / data.appliedRation) * 100;
        }
        
        setRationingInfo(rationData);
        setError(null);
      }
    } catch (error) {
      // console.error('Error refreshing rationing data:', error);
      setError('Failed to refresh rationing data.');
    } finally {
      setLoading(false);
    }
  };

  // Check if rationing is active (not in SUSPENDED state)
  const isRationingActive = rationingInfo.rationStatus !== 'SUSPENDED';

  return (
    <RationingContext.Provider
      value={{
        rationingInfo,
        loading,
        error,
        isRationingActive,
        refreshRationingData
      }}
    >
      {children}
    </RationingContext.Provider>
  );
};

export default RationingContext; 